import { motion } from "framer-motion";

const CHAPTERS = [
  {
    n: "01",
    title: "Ateliers, not warehouses.",
    body: "Every Atelier door is drawn, engineered, and finished within the ViewLux ateliers of Europe. Measured hands, patient timber, design that endures.",
  },
  {
    n: "02",
    title: "The material remembers.",
    body: "Solid wood. Aluminum-clad cores. Hand-selected veneers. Materials chosen to hold their character for a generation.",
  },
  {
    n: "03",
    title: "Made to your measure.",
    body: 'Nothing off the shelf. From a pivot entry to a 40" custom garage — every door leaves the atelier as yours.',
  },
];

export default function Manifesto() {
  return (
    <section
      id="manifesto"
      className="relative bg-brand-bone py-24 md:py-32"
      data-testid="manifesto-section"
    >
      <div className="max-w-[1280px] mx-auto px-6 md:px-10">
        <div className="flex items-center gap-4 mb-14 md:mb-20">
          <span className="w-12 h-px bg-brand-green/60" />
          <span className="text-[0.68rem] tracking-[0.28em] uppercase text-brand-green font-medium">
            The Manifesto
          </span>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 md:gap-16 items-start">
          <motion.h2
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-6 text-brand-green font-light leading-[1.08] tracking-[-0.02em] text-3xl md:text-4xl lg:text-5xl"
            data-testid="manifesto-title"
          >
            A door is a statement.
            <span className="block text-brand-gold">Not a fixture.</span>
          </motion.h2>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.9, delay: 0.15 }}
            className="lg:col-span-5 lg:col-start-8"
          >
            <p className="text-brand-ink/85 text-base md:text-lg leading-relaxed font-light">
              Atelier by ViewLux is a European-inspired collection of interior, exterior and garage
              doors — designed for architects, designers and homeowners who read a room in
              proportions, materials and light.
            </p>
            <p className="mt-4 text-brand-ink/60 text-sm md:text-base leading-relaxed font-light">
              Meticulously crafted using solid wood, aluminum-clad wood, and advanced engineered
              components — delivering exceptional durability, performance and beauty.
            </p>
          </motion.div>
        </div>

        {/* Chapters — clean 3-column, no oversized numerals */}
        <div className="mt-20 md:mt-28 grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-14">
          {CHAPTERS.map((c, i) => (
            <motion.div
              key={c.n}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.7, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] }}
              data-testid={`manifesto-chapter-${c.n}`}
            >
              <div className="flex items-center gap-3 mb-5">
                <span className="text-[0.7rem] tracking-[0.28em] uppercase text-brand-gold font-medium tabular-nums">
                  {c.n}
                </span>
                <span className="h-px flex-1 bg-brand-green/25" />
              </div>
              <h3 className="text-brand-green text-xl md:text-2xl font-normal leading-snug tracking-[-0.01em]">
                {c.title}
              </h3>
              <p className="mt-3 text-brand-ink/65 text-sm md:text-base leading-relaxed font-light">
                {c.body}
              </p>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Editorial 3-photo detail — from the Atelier catalog */}
      <div className="mt-20 md:mt-28 max-w-[1120px] mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-10 items-end mb-8 md:mb-10">
          <div className="md:col-span-7">
            <span className="text-[0.68rem] tracking-[0.28em] uppercase text-brand-gold font-medium">
              The Detail
            </span>
            <h4 className="mt-4 text-brand-green text-2xl md:text-3xl font-light leading-[1.1] tracking-[-0.01em]">
              European craftsmanship,
              <span className="block text-brand-gold">engineered to disappear.</span>
            </h4>
          </div>
          <p className="md:col-span-5 text-brand-ink/70 text-sm md:text-base leading-relaxed font-light">
            3D adjustable concealed hinges, knurled solid-bronze levers, and pivot-hung entrance
            doors — designed to feel weightless and silent in every moment of use.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-5">
          <motion.figure
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="md:col-span-6 md:row-span-2 relative overflow-hidden group"
            data-testid="detail-photo-0"
          >
            <img
              src="/pdf-embed/hero-door.jpg"
              alt="Pivot Entry · Aluminum-Clad Walnut"
              className="w-full aspect-[3/4] object-cover transition-transform duration-700 group-hover:scale-[1.03]"
              loading="lazy"
            />
            <figcaption className="mt-3 text-[0.6rem] tracking-[0.24em] uppercase text-brand-ink/60 font-medium">
              Pivot Entry · Aluminum-Clad Walnut
            </figcaption>
          </motion.figure>

          <motion.figure
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, delay: 0.08, ease: [0.22, 1, 0.36, 1] }}
            className="md:col-span-6 relative overflow-hidden group"
            data-testid="detail-photo-1"
          >
            <img
              src="/pdf-embed/hero-handle.jpg"
              alt="Knurled Bronze Lever"
              className="w-full aspect-[16/10] object-cover transition-transform duration-700 group-hover:scale-[1.03]"
              loading="lazy"
            />
            <figcaption className="mt-3 text-[0.6rem] tracking-[0.24em] uppercase text-brand-ink/60 font-medium">
              Knurled Bronze Lever
            </figcaption>
          </motion.figure>

          <motion.figure
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
            className="md:col-span-6 relative overflow-hidden group"
            data-testid="detail-photo-2"
          >
            <img
              src="/pdf-embed/hero-hinge.jpg"
              alt="Concealed 3D Hinge"
              className="w-full aspect-[4/3] object-cover object-bottom transition-transform duration-700 group-hover:scale-[1.03]"
              loading="lazy"
            />
            <figcaption className="mt-3 text-[0.6rem] tracking-[0.24em] uppercase text-brand-ink/60 font-medium">
              Concealed 3D Hinge · Silent · 3D Adjustable
            </figcaption>
          </motion.figure>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-4 mt-14 max-w-3xl">
          {[
            { k: "Premium", v: "Materials" },
            { k: "Superior", v: "Security" },
            { k: "Weather", v: "Resistant" },
            { k: "Timeless", v: "Design" },
          ].map((f) => (
            <div key={f.v} className="border-t border-brand-green/20 pt-3">
              <div className="text-[0.6rem] tracking-[0.28em] uppercase text-brand-gold font-medium">
                {f.k}
              </div>
              <div className="mt-1 text-brand-green text-base font-normal">{f.v}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
